import React, { useCallback, useEffect, useRef, useState } from "react";

import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "@/Components/ui";
import {
  CustomerRegistrationState,
  ServiceTable,
  TableOrder,
  TableOrderListParams,
  tableOrderService,
} from "@/api/services/table-order.service";
import { toast } from "react-hot-toast";

import { getErrorToastMessage } from "./getErrorToastMessage";

const orderTypeLabels: Record<TableOrder["order_type"], string> = {
  serving: "서빙",
  pickup: "픽업",
};

const paymentStatusLabels: Record<TableOrder["payment_status"], string> = {
  UNPAID: "미결제",
  PAYMENT_PENDING: "결제요청중",
  PAID: "결제완료",
  PAYMENT_FAILED: "결제실패",
  CANCEL_PENDING: "취소요청중",
  CANCELED: "취소완료",
};

const statusLabels: Record<TableOrder["status"], string> = {
  NEW: "신규",
  ACCEPTED: "접수",
  SERVED: "서빙완료",
  CANCELED: "주문취소",
};

const customerRegistrationLabels: Record<CustomerRegistrationState, string> = {
  NONE: "미연결",
  EXISTING_USER: "기존회원",
  AUTO_CREATED_USER: "자동생성",
};

const formatDateTime = (value: string | null) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const formatDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

type PreviousOrderLookupModalProps = {
  isOpen?: boolean;
  onClose?: () => void;
  onSelectOrder: (order: TableOrder) => Promise<void>;
  tables: ServiceTable[];
  currentOrderId?: number | null;
  embedded?: boolean;
};

const PreviousOrderLookupModal = ({
  isOpen = false,
  onClose,
  onSelectOrder,
  tables,
  currentOrderId = null,
  embedded = false,
}: PreviousOrderLookupModalProps) => {
  const [orderIdInput, setOrderIdInput] = useState("");
  const [customerPhoneInput, setCustomerPhoneInput] = useState("");
  const [orderType, setOrderType] = useState<TableOrder["order_type"] | "">("");
  const [selectedTableId, setSelectedTableId] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<TableOrder["payment_status"] | "">("");
  const [dateFrom, setDateFrom] = useState(() => formatDateInputValue(new Date()));
  const [dateTo, setDateTo] = useState(() => formatDateInputValue(new Date()));
  const [orders, setOrders] = useState<TableOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectingOrderId, setSelectingOrderId] = useState<number | null>(null);

  const handleSearch = useCallback(async () => {
    setLoading(true);
    try {
      const trimmedOrderId = orderIdInput.trim();
      if (trimmedOrderId) {
        const parsedOrderId = Number(trimmedOrderId);
        if (!Number.isInteger(parsedOrderId) || parsedOrderId <= 0) {
          toast.error("주문번호는 숫자로 입력해주세요.");
          setOrders([]);
          return;
        }

        const response = await tableOrderService.getTableOrder(parsedOrderId);
        setOrders(response.data ? [response.data] : []);
        return;
      }

      const params: TableOrderListParams = {};
      if (customerPhoneInput.trim()) {
        params.customer_phone = customerPhoneInput.replace(/[^0-9]/g, "");
      }
      if (orderType) {
        params.order_type = orderType;
      }
      if (selectedTableId) {
        params.table_id = selectedTableId;
      }
      if (paymentStatus) {
        params.payment_status = paymentStatus;
      }
      if (dateFrom) {
        params.date_from = dateFrom;
      }
      if (dateTo) {
        params.date_to = dateTo;
      }

      const response = await tableOrderService.listTableOrders(params);
      setOrders(response.data.results || []);
    } catch (error) {
      setOrders([]);
      toast.error(
        getErrorToastMessage(
          error,
          orderIdInput.trim() ? "해당 주문을 찾지 못했습니다." : "이전 주문 조회에 실패했습니다.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [customerPhoneInput, dateFrom, dateTo, orderIdInput, orderType, paymentStatus, selectedTableId]);

  const searchOnOpenRef = useRef(handleSearch);
  useEffect(() => {
    searchOnOpenRef.current = handleSearch;
  }, [handleSearch]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    void searchOnOpenRef.current();
  }, [isOpen]);

  const handleResetFilters = useCallback(() => {
    setOrderIdInput("");
    setCustomerPhoneInput("");
    setOrderType("");
    setSelectedTableId("");
    setPaymentStatus("");
    setDateFrom(formatDateInputValue(new Date()));
    setDateTo(formatDateInputValue(new Date()));
    setOrders([]);
  }, []);

  const handleSelect = useCallback(async (order: TableOrder) => {
    setSelectingOrderId(order.id);
    try {
      await onSelectOrder(order);
      onClose?.();
    } finally {
      setSelectingOrderId(null);
    }
  }, [onClose, onSelectOrder]);

  const content = (
    <>
      <ModalBody className="space-y-4 px-0 py-0!">
        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
          최근 현장주문을 기준으로 조회합니다. 주문번호를 입력하면 단건 조회가 우선 적용됩니다.
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          <div>
            <Label className="mb-2">주문번호</Label>
            <Input
              value={orderIdInput}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setOrderIdInput(event.target.value.replace(/[^0-9]/g, ""))}
              placeholder="예: 1024"
            />
          </div>
          <div>
            <Label className="mb-2">주문 타입</Label>
            <select className="form-select" value={orderType} onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setOrderType(event.target.value as TableOrder["order_type"] | "") }>
              <option value="">전체</option>
              <option value="serving">서빙</option>
              <option value="pickup">픽업</option>
            </select>
          </div>
          <div>
            <Label className="mb-2">고객 전화번호</Label>
            <Input
              value={customerPhoneInput}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCustomerPhoneInput(event.target.value.replace(/[^0-9-]/g, ""))}
              placeholder="예: 01012345678"
            />
          </div>
          <div>
            <Label className="mb-2">테이블</Label>
            <select className="form-select" value={selectedTableId} onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setSelectedTableId(event.target.value)}>
              <option value="">전체</option>
              {tables.map((table) => (
                <option key={table.id} value={String(table.id)}>
                  {table.group_name ? `${table.group_name} · ` : ""}
                  {table.name} ({table.code})
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="mb-2">결제 상태</Label>
            <select className="form-select" value={paymentStatus} onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setPaymentStatus(event.target.value as TableOrder["payment_status"] | "") }>
              <option value="">전체</option>
              {(Object.keys(paymentStatusLabels) as Array<TableOrder["payment_status"]>).map((status) => (
                <option key={status} value={status}>{paymentStatusLabels[status]}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="mb-2">시작일</Label>
            <Input type="date" value={dateFrom} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDateFrom(event.target.value)} />
          </div>
          <div>
            <Label className="mb-2">종료일</Label>
            <Input type="date" value={dateTo} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDateTo(event.target.value)} />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button color="primary" onClick={() => void handleSearch()} disabled={loading}>
            {loading ? "조회 중..." : "조회"}
          </Button>
          <Button color="soft-secondary" onClick={handleResetFilters} disabled={loading}>
            필터 초기화
          </Button>
        </div>

        <div className="max-h-[45vh] overflow-y-auto rounded-3xl border border-slate-200">
          {loading ? (
            <div className="p-6 text-center text-sm text-slate-500">주문을 조회하는 중입니다.</div>
          ) : orders.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">조건에 맞는 주문이 없습니다.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-slate-500">
                <tr>
                  <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">주문</th>
                  <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">타입/테이블</th>
                  <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">상태</th>
                  <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">금액</th>
                  <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">생성시각</th>
                  <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">선택</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orders.map((order) => {
                  const isCurrentOrder = currentOrderId === order.id;
                  return (
                    <tr key={order.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="p-4 align-middle text-slate-700">
                        <div className="font-semibold text-slate-800">#{order.id}</div>
                        <div className="text-xs text-slate-500">
                          고객: {order.customer_name || order.customer_phone_num || "미입력"}
                        </div>
                        <div className="text-xs text-slate-500">
                          연결: {customerRegistrationLabels[order.customer_registration_state] || "미연결"}
                        </div>
                      </td>
                      <td className="p-4 align-middle text-slate-700">
                        <div>{orderTypeLabels[order.order_type]}</div>
                        <div className="text-xs text-slate-500">
                          {order.table_name || (order.order_type === "pickup" ? "픽업" : "테이블 미지정")}
                        </div>
                      </td>
                      <td className="p-4 align-middle text-slate-700">
                        <div>{statusLabels[order.status]}</div>
                        <div className="text-xs text-slate-500">{paymentStatusLabels[order.payment_status]}</div>
                      </td>
                      <td className="p-4 align-middle text-slate-700">{order.total_amount.toLocaleString()}원</td>
                      <td className="p-4 align-middle text-slate-700">{formatDateTime(order.created_at)}</td>
                      <td className="p-4 align-middle text-slate-700">
                        <Button
                          color={isCurrentOrder ? "soft-secondary" : "soft-primary"}
                          size="sm"
                          disabled={isCurrentOrder || selectingOrderId === order.id}
                          onClick={() => void handleSelect(order)}
                        >
                          {isCurrentOrder ? "현재 주문" : selectingOrderId === order.id ? "불러오는 중..." : "불러오기"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </ModalBody>
      {!embedded ? (
        <ModalFooter>
          <Button color="light" onClick={onClose}>닫기</Button>
        </ModalFooter>
      ) : null}
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" variant="dialog" centered>
      <ModalHeader toggle={onClose}>이전 주문 불러오기</ModalHeader>
      {content}
    </Modal>
  );
};

export default PreviousOrderLookupModal;